import { REPORT, REPORT_RANGE } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, countUnique, sum } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useMemo } from 'react';
import { lagDateRange, createRowsFromGroupedDates, groupByKeys, getUniqueDates, getValues } from '@utils/data';
import { styleDateRange } from '@utils/styles';

interface CutGrowthByLocationProps {
    dateRange: { startDate: string; endDate: string };
}

export const CutGrowthByLocation = ({ dateRange }: CutGrowthByLocationProps) => {
    const adjustedRange = useMemo(() => lagDateRange(dateRange, 3), [dateRange]);

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.LOCATION_MONTHLY_CUT_VOLUME_GROWTH_BY_LOCATION,
        params: { ...adjustedRange, range: REPORT_RANGE.MONTHLY },
    });

    const groupedData = useMemo(() => {
        const groupedByLocation = groupByKeys(data, ['locationName', 'date']);
        const dateKeys = getUniqueDates(data, REPORT_RANGE.MONTHLY);
        return createRowsFromGroupedDates(groupedByLocation, dateKeys, 3);
    }, [data]);

    return (
        <BaseReport
            title={'Monthly Cut Volume Growth by Location'}
            description={
                'This table allows you to see the number of cuts for for a 4 month period by location, along with the % growth from the previous month for each, so you can identify locations which are growing or shrinking. Note that if you select more than 1 month in the date range above, each Reseller will have multiple rows (one for each full month).'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: [
                    'Location Name',
                    'Date',
                    'Successful Cuts',
                    '% Growth',
                    'Cuts 1 Month Prior',
                    '% Growth',
                    'Cuts 2 Months Prior',
                    '% Growth',
                    'Cuts 3 Months Prior',
                    '% Growth',
                ],
                data: groupedData.map((items) => [
                    items[0]?.locationName || 'Error: Could Not Find Location Name',
                    items[0]?.date || 'Error: Could Not Find Date',
                    ...getValues(items.slice(1), ['successfulCuts', 'pctGrowth']),
                ]),
                summaries: [countUnique, countUnique, sum, average, sum, average, sum, average, sum, average],
                sort: [
                    basicSort,
                    dateSort(REPORT_RANGE.MONTHLY),
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                ],
            }}
        />
    );
};
